import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { stripePromise, PRICING_INFO } from '../../config/stripeConfig';

interface SubscriptionData {
  subscriptionStatus: 'active' | 'inactive' | 'cancelled' | 'canceling';
  subscriptionType: 'monthly' | 'yearly';
  stripePriceId: string;
  stripePaymentMethodId: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodEnd?: any;
  currentPeriodStart?: any;
  lastPaymentAt?: any;
  paymentMethodDetails?: {
    last4: string;
    brand: string;
    expiryMonth: number;
    expiryYear: number;
    cardholderName?: string;
  };
  pendingPlanChange?: {
    oldPriceId: string;
    oldSubscriptionType: 'monthly' | 'yearly';
    newPriceId: string;
    newSubscriptionType: 'monthly' | 'yearly';
    effectiveDate: any;
    scheduledAt: any;
    isSameInterval: boolean;
  };
}

interface StripeSubscriptionData {
  subscription: {
    id: string;
    status: string;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
    canceledAt: number | null;
  };
  paymentMethod: {
    last4: string;
    brand: string;
    expiryMonth: number;
    expiryYear: number;
    cardholderName?: string;
  } | null;
  invoices: Array<{
    id: string;
    amount: number;
    currency: string;
    date: number;
    status: string;
  }>;
}

type UiStatus = 'active' | 'inactive' | 'canceling' | 'cancelled';

function formatDateMaybe(value: any): string {
  if (!value) return '-';
  if (value?.toDate)
    return value.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  if (typeof value === 'number') {
    return new Date(value * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  const d = new Date(value);
  if (!isNaN(d.getTime()))
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  return '-';
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'SGD',
  }).format(amount);
}

/**
 * Prefer Stripe as source of truth when available, otherwise fall back to Firestore.
 */
function deriveUiStatus(
  firestoreStatus: SubscriptionData['subscriptionStatus'] | undefined,
  stripe: StripeSubscriptionData | null
): UiStatus {
  const stripeStatusRaw = stripe?.subscription?.status;
  const stripeCancelAtPeriodEnd = stripe?.subscription?.cancelAtPeriodEnd;

  if (stripeStatusRaw === 'canceled') return 'cancelled';
  if (stripeCancelAtPeriodEnd) return 'canceling';
  if (stripeStatusRaw === 'active' || stripeStatusRaw === 'trialing')
    return 'active';

  if (firestoreStatus === 'canceling') return 'canceling';
  if (firestoreStatus === 'cancelled') return 'cancelled';
  if (firestoreStatus === 'active') return 'active';

  return 'inactive';
}

// Enhanced Payment Method Update Form Component - Matches Mobile Version
const UpdatePaymentForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const [cardholderName, setCardholderName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const functions = getFunctions(undefined, 'asia-southeast1');
  const updatePaymentMethodFunc = httpsCallable(
    functions,
    'updatePaymentMethod'
  );

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!cardholderName.trim()) {
      setError('Card holder name is required');
      return;
    }

    if (!cardComplete) {
      setError('Please enter valid card details');
      return;
    }

    if (!stripe || !elements) return;

    setUpdating(true);
    setError('');

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const billingDetails: any = {
        name: cardholderName.trim(),
      };

      if (user?.email) {
        billingDetails.email = user.email;
      }

      if (postalCode.trim()) {
        billingDetails.address = {
          postal_code: postalCode.trim(),
        };
      }

      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: billingDetails,
        });

      if (pmError) {
        throw new Error(pmError.message);
      }

      await updatePaymentMethodFunc({
        paymentMethodId: paymentMethod.id,
      });

      onSuccess();
    } catch (err: any) {
      console.error('Error updating payment method:', err);
      setError(err?.message || 'Failed to update payment method');
    } finally {
      setUpdating(false);
    }
  };

  const cardElementOptions = {
    hidePostalCode: true, // Hide built-in postal code - we have our own optional field
    style: {
      base: {
        fontSize: '16px',
        color: '#1f2937',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#dc2626',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Card Holder Name */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Card Holder Name
        </label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Card holder name"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-gray-900 placeholder-gray-400"
        />
      </div>

      {/* Card Details */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Card details
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm">
          <CardElement
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          We never store your full card data.
        </p>
      </div>

      {/* Postal Code (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Postal Code (Optional)
        </label>
        <input
          type="text"
          value={postalCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setPostalCode(value);
          }}
          placeholder="Postal code"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-gray-900 placeholder-gray-400"
          inputMode="numeric"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={updating}
          className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={updating || !stripe || !cardComplete}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-semibold"
        >
          {updating ? 'Saving...' : 'Save new payment method'}
        </button>
      </div>
    </form>
  );
};

// Resubscribe Form Component (for cancelled/inactive users)
const ResubscribeForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>(
    'monthly'
  );
  const [cardholderName, setCardholderName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');

  const functions = getFunctions(undefined, 'asia-southeast1');
  const createSubscriptionFunc = httpsCallable(functions, 'createSubscription');

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cardholderName.trim()) {
      setError('Card holder name is required');
      return;
    }

    if (!cardComplete) {
      setError('Please enter valid card details');
      return;
    }

    if (!stripe || !elements) return;

    setSubscribing(true);
    setError('');

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const billingDetails: any = {
        name: cardholderName.trim(),
      };

      if (user?.email) {
        billingDetails.email = user.email;
      }

      if (postalCode.trim()) {
        billingDetails.address = {
          postal_code: postalCode.trim(),
        };
      }

      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: billingDetails,
        });

      if (pmError) {
        throw new Error(pmError.message);
      }

      const priceId = PRICING_INFO[selectedPlan].priceId;

      await createSubscriptionFunc({
        paymentMethodId: paymentMethod.id,
        priceId: priceId,
      });

      onSuccess();
    } catch (err: any) {
      console.error('Error resubscribing:', err);
      setError(err?.message || 'Failed to resubscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const cardElementOptions = {
    hidePostalCode: true, // Hide built-in postal code - we have our own optional field
    style: {
      base: {
        fontSize: '16px',
        color: '#1f2937',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#dc2626',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Plan Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-3">
          Select Plan
        </label>
        <div className="space-y-3">
          {/* Monthly Plan */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition ${
              selectedPlan === 'monthly'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedPlan('monthly')}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold text-gray-900">Monthly Plan</h4>
                <p className="text-sm text-gray-600 mt-1">
                  {PRICING_INFO.monthly.displayPrice} per month
                </p>
              </div>
              {selectedPlan === 'monthly' && (
                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Yearly Plan */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition ${
              selectedPlan === 'yearly'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedPlan('yearly')}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900">Yearly Plan</h4>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                    {PRICING_INFO.yearly.savingsPercentage} off
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {PRICING_INFO.yearly.displayPrice} per year
                </p>
                <p className="text-xs text-green-600 font-medium mt-1">
                  {PRICING_INFO.yearly.savings} • Just{' '}
                  {PRICING_INFO.yearly.monthlyEquivalent}
                </p>
              </div>
              {selectedPlan === 'yearly' && (
                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Holder Name */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Card Holder Name
        </label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Card holder name"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-gray-900 placeholder-gray-400"
        />
      </div>

      {/* Card Details */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Card details
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm">
          <CardElement
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          We never store your full card data.
        </p>
      </div>

      {/* Postal Code (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Postal Code (Optional)
        </label>
        <input
          type="text"
          value={postalCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setPostalCode(value);
          }}
          placeholder="Postal code"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-gray-900 placeholder-gray-400"
          inputMode="numeric"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={subscribing}
          className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={subscribing || !stripe || !cardComplete}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-semibold"
        >
          {subscribing ? 'Processing...' : 'Resubscribe'}
        </button>
      </div>
    </form>
  );
};

const SubscriptionPage: React.FC = () => {
  const { user } = useAuth();

  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionData | null>(null);
  const [stripeData, setStripeData] = useState<StripeSubscriptionData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showResubscribeModal, setShowResubscribeModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const functions = getFunctions(undefined, 'asia-southeast1');

  useEffect(() => {
    if (!user?.uid) return;
    fetchSubscriptionData();
  }, [user?.uid]);

  const fetchSubscriptionData = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSubscriptionData({
          subscriptionStatus: data.subscriptionStatus || 'inactive',
          subscriptionType: data.subscriptionType || 'monthly',
          stripePriceId: data.stripePriceId || '',
          stripePaymentMethodId: data.stripePaymentMethodId || '',
          stripeCustomerId: data.stripeCustomerId,
          stripeSubscriptionId: data.stripeSubscriptionId,
          currentPeriodEnd: data.currentPeriodEnd,
          currentPeriodStart: data.currentPeriodStart,
          lastPaymentAt: data.lastPaymentAt,
          paymentMethodDetails: data.paymentMethodDetails,
          pendingPlanChange: data.pendingPlanChange, // Add this line
        });
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeSubscription = async () => {
    if (!user?.uid) return;
    try {
      const getSubscriptionFunc = httpsCallable<any, StripeSubscriptionData>(
        functions,
        'getSubscriptionDetails'
      );
      const result = await getSubscriptionFunc();
      console.log('Stripe subscription data:', result.data);
      console.log('Payment method:', result.data?.paymentMethod);
      console.log('Invoices:', result.data?.invoices);
      setStripeData(result.data);

      // ✅ FIX: Only update payment method if it's different to prevent re-renders
      if (result.data?.paymentMethod) {
        setSubscriptionData((prev) => {
          if (!prev) return prev;
          // Only update if payment method is different
          if (
            prev.paymentMethodDetails?.last4 ===
              result.data.paymentMethod?.last4 &&
            prev.paymentMethodDetails?.brand ===
              result.data.paymentMethod?.brand
          ) {
            return prev; // No change needed
          }
          return {
            ...prev,
            paymentMethodDetails: result.data.paymentMethod || undefined,
          };
        });
      }
    } catch (error) {
      console.error('Error fetching Stripe subscription:', error);
    }
  };

  // ✅ FIX: Only fetch Stripe data once when subscription becomes active
  useEffect(() => {
    if (
      subscriptionData &&
      subscriptionData.subscriptionStatus !== 'inactive' &&
      !stripeData // Only fetch if we don't already have stripe data
    ) {
      fetchStripeSubscription();
    }
  }, [subscriptionData?.subscriptionStatus]); // ✅ Only depend on status, not entire object

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const cancelFunc = httpsCallable(functions, 'cancelSubscription');
      await cancelFunc();
      await fetchSubscriptionData();
      await fetchStripeSubscription();
      setShowCancelModal(false);
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      alert(error?.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      const reactivateFunc = httpsCallable(functions, 'reactivateSubscription');
      await reactivateFunc();
      await fetchSubscriptionData();
      await fetchStripeSubscription();
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      alert(error?.message || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const handlePaymentUpdateSuccess = async () => {
    setShowUpdatePaymentModal(false);
    await fetchSubscriptionData();
    await fetchStripeSubscription();
  };

  const handleResubscribeSuccess = async () => {
    setShowResubscribeModal(false);
    await fetchSubscriptionData();
    await fetchStripeSubscription();
  };

  const handleChangePlan = async (newPriceId: string) => {
    setChangingPlan(true);
    try {
      const changePlanFunc = httpsCallable(functions, 'changeSubscriptionPlan');
      const result = await changePlanFunc({ newPriceId });

      console.log('Plan change result:', result.data);

      await fetchSubscriptionData();
      await fetchStripeSubscription();
      setShowChangePlanModal(false);

      // Show success message
      alert(
        `Your plan will change at the end of your current billing period on ${formatDateMaybe(subscriptionData?.currentPeriodEnd)}`
      );
    } catch (error: any) {
      console.error('Error changing plan:', error);
      alert(error?.message || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  const handleCancelPendingPlanChange = async () => {
    try {
      const cancelFunc = httpsCallable(functions, 'cancelPendingPlanChange');
      await cancelFunc();
      await fetchSubscriptionData();
      alert('Pending plan change cancelled');
    } catch (error: any) {
      console.error('Error cancelling plan change:', error);

      // ✅ Better error handling for CORS and network issues
      let errorMessage = 'Failed to cancel plan change';
      if (error.code === 'internal' || error.message?.includes('CORS')) {
        errorMessage =
          'Network error. Please check your Cloud Function CORS settings.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  const uiStatus = deriveUiStatus(
    subscriptionData?.subscriptionStatus,
    stripeData
  );
  const currentSubscriptionType = stripeData?.subscription
    ? subscriptionData?.subscriptionType
    : subscriptionData?.subscriptionType || 'monthly';

  const paymentMethod =
    stripeData?.paymentMethod || subscriptionData?.paymentMethodDetails;
  const renewalDate =
    stripeData?.subscription?.currentPeriodEnd ||
    subscriptionData?.currentPeriodEnd;
  const transactions = stripeData?.invoices || [];

  console.log('UI Status:', uiStatus);
  console.log('Payment Method:', paymentMethod);
  console.log('Transactions:', transactions);
  console.log('Stripe Data:', stripeData);
  console.log('Subscription Data:', subscriptionData);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Subscription</h1>
        </div>

        {/* Subscription Status Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  Unveil{' '}
                  {currentSubscriptionType === 'monthly' ? 'Monthly' : 'Yearly'}{' '}
                  Plan
                </h2>
                {uiStatus === 'active' && (
                  <span className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                    Active
                  </span>
                )}
              </div>
              {renewalDate && uiStatus === 'active' && (
                <p className="text-gray-600">
                  Your plan will renew on {formatDateMaybe(renewalDate)}.
                </p>
              )}
              {renewalDate && uiStatus === 'canceling' && (
                <p className="text-gray-600">
                  Your plan will end on {formatDateMaybe(renewalDate)}.
                </p>
              )}
              {(uiStatus === 'cancelled' || uiStatus === 'inactive') && (
                <p className="text-gray-600">
                  You don't have an active subscription. Resubscribe to access
                  premium features.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {uiStatus === 'active' && (
                <>
                  <button
                    onClick={() => setShowChangePlanModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                  >
                    Change Plan
                  </button>
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                  >
                    Cancel Subscription
                  </button>
                </>
              )}
              {uiStatus === 'canceling' && (
                <>
                  <button
                    onClick={() => setShowChangePlanModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                  >
                    Change Plan
                  </button>
                  <button
                    onClick={handleReactivateSubscription}
                    disabled={reactivating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50"
                  >
                    {reactivating ? 'Reactivating...' : 'Resubscribe'}
                  </button>
                </>
              )}
              {(uiStatus === 'cancelled' || uiStatus === 'inactive') && (
                <button
                  onClick={() => setShowResubscribeModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                >
                  Resubscribe
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Pending Plan Change Banner */}
        {subscriptionData?.pendingPlanChange && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-1">
                    Plan Change Scheduled
                  </h3>
                  <p className="text-blue-800">
                    Your plan will change from{' '}
                    <strong>
                      {currentSubscriptionType === 'monthly'
                        ? 'Monthly'
                        : 'Yearly'}
                    </strong>{' '}
                    to{' '}
                    <strong>
                      {subscriptionData.pendingPlanChange
                        .newSubscriptionType === 'monthly'
                        ? 'Monthly'
                        : 'Yearly'}
                    </strong>{' '}
                    on{' '}
                    <strong>
                      {formatDateMaybe(
                        subscriptionData.pendingPlanChange.effectiveDate
                      )}
                    </strong>
                  </p>
                  <p className="text-sm text-blue-700 mt-2">
                    You'll continue to have full access to your current{' '}
                    {currentSubscriptionType === 'monthly'
                      ? 'Monthly'
                      : 'Yearly'}{' '}
                    plan until then. No charges will be made until your new plan
                    starts.
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancelPendingPlanChange}
                className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition text-sm font-medium flex-shrink-0"
              >
                Cancel Change
              </button>
            </div>
          </div>
        )}

        {/* Transactions and Payment Method Grid */}
        {(uiStatus === 'active' || uiStatus === 'canceling') && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transactions card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Transactions
              </h3>

              {transactions.length === 0 ? (
                <p className="text-sm text-gray-600">No transactions yet.</p>
              ) : (
                <div className="space-y-3">
                  {transactions.map((t) => (
                    <div key={t.id} className="flex justify-between">
                      <span className="text-sm text-gray-700">
                        {formatDateMaybe(t.date)}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(t.amount, t.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* How you pay card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                How you pay
              </h3>

              <div className="mb-4">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Card Holder Name
                    </p>
                    <p className="text-sm text-gray-900">
                      {paymentMethod?.cardholderName || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Card Details</p>
                    {paymentMethod ? (
                      <>
                        <p className="text-sm text-gray-900 capitalize">
                          {paymentMethod.brand} ****{paymentMethod.last4}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Expires{' '}
                          {String(paymentMethod.expiryMonth).padStart(2, '0')}/
                          {paymentMethod.expiryYear}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-900">
                          No payment method
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Add a payment method above
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowUpdatePaymentModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
              >
                Change payment method
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Cancel Subscription
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel? You'll keep access until the end
              of your current billing period.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {cancelling ? 'Canceling...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Payment Modal */}
      {showUpdatePaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">
              Change Payment Method
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Update your card details securely using Stripe.
            </p>

            <Elements stripe={stripePromise}>
              <UpdatePaymentForm
                onSuccess={handlePaymentUpdateSuccess}
                onCancel={() => setShowUpdatePaymentModal(false)}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* Resubscribe Modal (for cancelled/inactive users - requires card) */}
      {showResubscribeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">
              Resubscribe to Unveil
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Choose your plan and enter your payment details to reactivate your
              subscription.
            </p>

            <Elements stripe={stripePromise}>
              <ResubscribeForm
                onSuccess={handleResubscribeSuccess}
                onCancel={() => setShowResubscribeModal(false)}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Change Your Plan
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Your plan will change at the end of your current billing period on{' '}
              <strong>
                {formatDateMaybe(subscriptionData?.currentPeriodEnd)}
              </strong>
              . You'll continue to have full access until then.
            </p>

            <div className="space-y-4">
              {/* Monthly Plan Option */}
              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                  currentSubscriptionType === 'monthly'
                    ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                    : subscriptionData?.pendingPlanChange
                          ?.newSubscriptionType === 'monthly'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => {
                  if (currentSubscriptionType !== 'monthly') {
                    handleChangePlan(PRICING_INFO.monthly.priceId);
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Monthly Plan
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {PRICING_INFO.monthly.displayPrice} per month
                    </p>
                  </div>
                  {currentSubscriptionType === 'monthly' && (
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs rounded-full">
                      Current
                    </span>
                  )}
                  {subscriptionData?.pendingPlanChange?.newSubscriptionType ===
                    'monthly' && (
                    <span className="px-3 py-1 bg-green-600 text-white text-xs rounded-full">
                      Scheduled
                    </span>
                  )}
                </div>
              </div>

              {/* Yearly Plan Option */}
              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                  currentSubscriptionType === 'yearly'
                    ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                    : subscriptionData?.pendingPlanChange
                          ?.newSubscriptionType === 'yearly'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => {
                  if (currentSubscriptionType !== 'yearly') {
                    handleChangePlan(PRICING_INFO.yearly.priceId);
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900">
                        Yearly Plan
                      </h4>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        {PRICING_INFO.yearly.savingsPercentage} off
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {PRICING_INFO.yearly.displayPrice} per year
                    </p>
                    <p className="text-xs text-green-600 font-medium mt-1">
                      {PRICING_INFO.yearly.savings} • Just{' '}
                      {PRICING_INFO.yearly.monthlyEquivalent}
                    </p>
                  </div>
                  {currentSubscriptionType === 'yearly' && (
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs rounded-full">
                      Current
                    </span>
                  )}
                  {subscriptionData?.pendingPlanChange?.newSubscriptionType ===
                    'yearly' && (
                    <span className="px-3 py-1 bg-green-600 text-white text-xs rounded-full">
                      Scheduled
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>How it works:</strong> Your current plan will continue
                until {formatDateMaybe(subscriptionData?.currentPeriodEnd)}.
                Your new plan will automatically start after that, and you'll be
                charged then.
              </p>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowChangePlanModal(false)}
                disabled={changingPlan}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Close
              </button>
            </div>

            {changingPlan && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Scheduling plan change...
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
